import { useEffect } from "react";
import { HERO_IMAGE } from "../data/mock";
import { Clock, ShieldCheck } from "lucide-react";

export default function PrintPage() {
  useEffect(() => { document.title = "Agent ŠVP — Vytvořte kvalitní ŠVP za pár hodin"; }, []);

  return (
    <>
      <style>{`
        @page { size: A4 landscape; margin: 0; }
        @media print {
          body { margin: 0; padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .no-print { display: none !important; }
        }
      `}</style>

      {/* Print button */}
      <div className="no-print fixed top-4 right-4 z-50">
        <button
          onClick={() => window.print()}
          className="bg-[#0049B3] text-white px-6 py-3 rounded-xl font-bold text-sm shadow-lg hover:bg-[#003380] transition-colors"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          Vytisknout / Uložit jako PDF
        </button>
      </div>

      <div
        className="relative overflow-hidden bg-gradient-to-br from-[#F8FAFF] via-white to-[#FFF9EB]"
        style={{ width: '297mm', height: '210mm', margin: '0 auto' }}
      >
        {/* Decorative */}
        <div className="absolute top-8 left-8 w-48 h-48 bg-[#0049B3]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-8 right-8 w-56 h-56 bg-[#FFC72C]/8 rounded-full blur-3xl" />
        <div className="absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: 'radial-gradient(circle, #0049B3 1px, transparent 1px)',
          backgroundSize: '30px 30px'
        }} />

        <div className="relative h-full flex items-center px-16 py-10">
          <div className="flex items-center gap-10 w-full">
            {/* Left */}
            <div className="flex-1 space-y-5">
              {/* Headline */}
              <h1
                className="text-5xl font-extrabold text-[#0F1B3D]"
                style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", lineHeight: '1.1' }}
              >
                Přestaňte ztrácet
                <br />
                <span style={{ color: '#0049B3' }}>týdny prací</span> na novém ŠVP.
              </h1>
              <h2
                className="text-2xl font-bold text-[#0F1B3D]/80"
                style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
              >
                Vytvořte kvalitní ŠVP <span style={{ color: '#FFC72C' }}>za pár hodin.</span>
              </h2>
              <p
                className="text-lg font-semibold"
                style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: '#0049B3' }}
              >
                V souladu s RVP PV 2025 a požadavky ČŠI.
              </p>

              {/* Description */}
              <p
                className="text-sm text-[#4A5B7A] max-w-md leading-relaxed"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Inteligentní AI aplikace, která kombinuje umělou inteligenci s expertními znalostmi
                RVP PV 2025 a šetří ředitelům škol až stovky hodin práce. Celým procesem vás provede chytrý AI agent krok za krokem.
              </p>

              {/* Website URL */}
              <div>
                <a
                  href="https://agentsvp.cz"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 rounded-2xl px-8 py-4 hover:opacity-90 transition-opacity"
                  style={{ backgroundColor: '#0049B3', textDecoration: 'none' }}
                >
                  <span
                    className="text-2xl font-extrabold text-white tracking-wide"
                    style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                  >
                    Více informací na agentsvp.cz
                  </span>
                </a>
              </div>

              {/* Trust indicators */}
              <div className="flex flex-wrap gap-5 pt-1">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(0,73,179,0.08)' }}>
                    <Clock className="w-3.5 h-3.5 text-[#0049B3]" />
                  </div>
                  <span className="text-xs font-medium text-[#3D4F6F]" style={{ fontFamily: "'Inter', sans-serif" }}>
                    1-2 hodiny místo týdnů
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(0,73,179,0.08)' }}>
                    <ShieldCheck className="w-3.5 h-3.5 text-[#0049B3]" />
                  </div>
                  <span className="text-xs font-medium text-[#3D4F6F]" style={{ fontFamily: "'Inter', sans-serif" }}>
                    V souladu s RVP PV 2025 a požadavky ČŠI
                  </span>
                </div>
              </div>
            </div>

            {/* Right - Hero + floating cards */}
            <div className="flex-shrink-0 relative">
              <img
                src={HERO_IMAGE}
                alt="Agent ŠVP"
                className="w-[300px]"
                style={{ mixBlendMode: 'multiply' }}
              />

              {/* Floating cards */}
              <div className="absolute -left-6 top-[40%] bg-white/90 rounded-xl px-3 py-2 shadow-lg border border-gray-100" style={{ backdropFilter: 'blur(8px)' }}>
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#ecfdf5' }}>
                    <Clock className="w-3.5 h-3.5" style={{ color: '#059669' }} />
                  </div>
                  <div>
                    <div className="text-[10px] font-bold text-[#0F1B3D]">Úspora času</div>
                    <div className="text-base font-extrabold" style={{ color: '#059669' }}>95%</div>
                  </div>
                </div>
              </div>

              <div className="absolute -right-6 top-[65%] bg-white/90 rounded-xl px-3 py-2 shadow-lg border border-gray-100" style={{ backdropFilter: 'blur(8px)' }}>
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(0,73,179,0.1)' }}>
                    <ShieldCheck className="w-3.5 h-3.5 text-[#0049B3]" />
                  </div>
                  <div>
                    <div className="text-[9px] font-bold text-[#0F1B3D] leading-tight">V souladu s</div>
                    <div className="text-xs font-extrabold text-[#0049B3] leading-tight">RVP PV 2025</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom contact info */}
        <div className="absolute bottom-5 left-16 right-16">
          <div className="flex items-center justify-between text-[11px] text-[#6B7A99] border-t border-[#E8EDF5] pt-4" style={{ fontFamily: "'Inter', sans-serif" }}>
            <span>#TouchOfLucky s.r.o. | IČO: 09094075</span>
            <div className="flex items-center gap-5">
              <span>+420 739 921 359</span>
              <span>info@agentsvp.cz</span>
              <span>Zámostní 1155/27, Slezská Ostrava, 710 00 Ostrava</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
